import type { ComponentChildren } from 'preact'
import styles from './Button.module.css'

interface ButtonProps {
  children: ComponentChildren
  /* The event is passed through so a caller can keep a reference to the
     control it came from — focus restoration needs to know where to return
     to after a screen change. */
  onClick?: (event: MouseEvent) => void
  type?: 'button' | 'submit'
  variant?: 'primary' | 'secondary'
  disabled?: boolean
  /** Renders a busy state and blocks repeat activation. */
  busy?: boolean
  fullWidth?: boolean
  /**
   * Accessible name, when the visible text is not specific enough on its own.
   *
   * A list of rows each holding a "Delete" button reads as several identically
   * named controls to a screen reader; this is what tells them apart.
   */
  label?: string
  /** What the busy state announces. Not every button is saving something. */
  busyLabel?: string
  /**
   * Renders the button as a toggle, announcing whether it is the chosen one.
   *
   * Added at C6.4 for the daily check's three answers, where the selected state
   * was carried by `variant` alone — a colour difference and nothing else, which
   * is exactly what "never communicate state by colour alone" forbids. A screen
   * reader had no way to tell which of the three had been chosen.
   *
   * Left `undefined` on an ordinary button rather than defaulting to `false`:
   * `aria-pressed="false"` turns every button in the app into a toggle that
   * happens to be off, which is a different control from a button.
   */
  pressed?: boolean
  /**
   * The rendered element, for callers that need to move focus to it.
   *
   * An explicit prop rather than `ref`: a Preact function component does not
   * accept one without `preact/compat`, which is not in the pinned stack.
   */
  elementRef?: (element: HTMLButtonElement | null) => void
}

export function Button({
  children,
  onClick,
  type = 'button',
  variant = 'primary',
  disabled = false,
  busy = false,
  fullWidth = false,
  label,
  busyLabel = 'Saving…',
  pressed,
  elementRef,
}: ButtonProps) {
  const className = [styles.button, styles[variant], fullWidth ? styles.fullWidth : '']
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type={type === 'submit' ? 'submit' : 'button'}
      class={className}
      {...(elementRef === undefined ? {} : { ref: elementRef })}
      onClick={onClick}
      disabled={disabled || busy}
      aria-label={label}
      // Busy state is announced, not just shown — never colour or motion alone.
      aria-busy={busy ? 'true' : undefined}
      aria-pressed={pressed === undefined ? undefined : pressed ? 'true' : 'false'}
    >
      {busy ? busyLabel : children}
    </button>
  )
}
